import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Paper,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";

const Chat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [products, setProducts] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamText, setStreamText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [eventSource, setEventSource] = useState(null);
  const messagesEndRef = useRef(null);

  // useEffect(() => {
  //   if (open) {
  //     // fetchMessages();
  //   }
  // }, [open]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = () => {
    if (isStreaming) return;
    if (newMessage.trim()) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          chatResponse: { text: newMessage },
          sender: "user",
          timestamp: new Date().toISOString(),
        },
      ]);
      scrollToBottom(); // Add this line

      try {
        setLoading(true);

        const newEventSource = new EventSource(
          process.env.REACT_APP_API_URL +
            `/api/llm/search?message=${encodeURIComponent(newMessage)}`
        );
        let accumulatedResponse = "";

        newEventSource.onmessage = (event) => {
          if (event.data === "[DONE]") {
            newEventSource.close();
            setIsStreaming(false);
            setEventSource(null);
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                chatResponse: {
                  text: accumulatedResponse,
                },
                sender: "assistant",
                timestamp: new Date().toISOString(),
              },
            ]);
            scrollToBottom();

            // Clear the streamed message
            setStreamText("");
            setLoading(false);
          } else {
            let result;
            try {
              result = JSON.parse(event.data);
              switch (result.type) {
                case "products":
                  setProducts(result.content);
                  console.log("result", result);
                  break;
                case "answer_chunk":
                  accumulatedResponse += result.content;
                  setStreamText((prevText) => prevText + result.content);
                  scrollToBottom();
                  break;
                case "error":
                  console.error("Error en el stream:", data.content);
                  eventSource.close();
                  break;
              }
            } catch (e) {
              console.error("No es JSON válido", e);
            }
          }
        };

        newEventSource.onerror = (error) => {
          console.error("EventSource failed:", error);
          newEventSource.close();
          setIsStreaming(false);
          setError(error.message);
          setLoading(false);
        };

        setEventSource(newEventSource);
        setIsStreaming(true);
      } catch (error) {
        console.error("Error sending message:", error);
        setError("Error enviando mensaje");
      }
      setNewMessage("");
    }
  };

  const stopStream = () => {
    if (eventSource) {
      eventSource.close();
      setIsStreaming(false);
      setEventSource(null);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          chatResponse: { text: streamText },
          sender: "assistant",
          timestamp: new Date().toISOString(),
        },
      ]);
      scrollToBottom();
      setStreamText("");
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  const renderMessageContent = (message) => {
    if (message.sender === "user") {
      return message.chatResponse.text;
    } else {
      return (
        <ReactMarkdown
          components={{
            a: ({ node, ...props }) => (
              <a target="_blank" rel="noopener noreferrer" {...props} />
            ),
          }}
        >
          {message.chatResponse.text}
        </ReactMarkdown>
      );
    }
  };

  return (
    <div>
      {/* INI CHAT */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleClickOpen}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
        }}
      >
        <ChatIcon />
      </Fab>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Chat
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              height: "50vh",
              overflowY: "auto",
              p: 2,
              mb: 2,
              position: "relative",
            }}
          >
            {loading && !streamText && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems:
                    message.sender === "user" ? "flex-end" : "flex-start",
                  mb: 1,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    maxWidth: "70%",
                    bgcolor:
                      message.sender === "user" ? "primary.light" : "grey.100",
                  }}
                >
                  {renderMessageContent(message)}
                </Paper>
                <Typography variant="caption" sx={{ mt: 0.5 }}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </Typography>
              </Box>
            ))}

            {/* Display currently streaming message */}
            {streamText && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    maxWidth: "70%",
                    bgcolor: "grey.100",
                  }}
                >
                  <Typography>{streamText}</Typography>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Paper>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Escribe un mensaje..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={loading}
            />
            <Button
              variant="contained"
              onClick={
                isStreaming && streamText ? stopStream : handleSendMessage
              }
            >
              {isStreaming && streamText ? "Stop" : "Enviar"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      {/* FIN CHAT */}
    </div>
  );
};

export default Chat;
